'use client';

import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useUser } from 'app/_providers/User';
import useAccess from 'app/_utils/data/access';

import * as Sentry from '@sentry/nextjs';
import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { MemberAccessCheckRequestItem, MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

/** Permission cache for a specific organization or system-wide */
interface PermissionCache {
  organization?: Record<string, boolean>;
  workspace?: Record<string, boolean>;
  product?: Record<string, boolean>;
  system?: Record<string, boolean>;
}

/** Cache structure organized by organization ID */
interface OrganizationPermissionCache {
  [organizationId: string]: PermissionCache;
}

/** Loading state tracking for permission requests */
interface LoadingState {
  requests: Set<string>;
}

/** Context interface for the global access provider */
interface GlobalAccessContextType {
  /** Get permissions for specific access checks (returns cached data if available) */
  getPermissions: (accessChecks: MemberAccessCheckRequestItem[]) => PermissionCache | null;
  /** Request permissions from the API and returns the permissions */
  requestPermissions: (accessChecks: MemberAccessCheckRequestItem[]) => Promise<PermissionCache>;
  /** Check if permissions are currently being loaded */
  isLoadingPermissions: (accessChecks: MemberAccessCheckRequestItem[]) => boolean;
  /** Clear all cached permissions (useful for logout or org change) */
  clearCache: () => void;
}

/**
 * Creates a unique loading key for permission requests
 * @param accessChecks - The access checks to create a key for
 * @param organizationId - Current organization ID (optional for system permissions)
 * @returns A unique string key for tracking loading state
 */
function createLoadingKey(accessChecks: MemberAccessCheckRequestItem[], organizationId?: string): string {
  return accessChecks.map((check) => {
    if (check.accessType === MemberAccessCheckType.System) {
      return `system:${check.checks?.join(',') || ''}`;
    }

    return `${organizationId || 'no-org'}:${check.accessType}:${check.checks?.join(',') || ''}`;
  }).join('|');
}

/**
 * Checks if all requested permissions are available in the cache
 * @param accessChecks - The access checks to verify
 * @param orgCache - Organization-specific cache
 * @param systemCache - System-wide cache
 * @param organizationId - Current organization ID
 * @returns True if all permissions are cached, false otherwise
 */
function areAllPermissionsCached(
  accessChecks: MemberAccessCheckRequestItem[],
  orgCache: OrganizationPermissionCache,
  systemCache: PermissionCache,
  organizationId?: string,
): boolean {
  return accessChecks.every((check) => {
    if (!check.checks) {
      return true;
    }

    const permissionType = check.accessType;

    // System permissions are stored separately
    if (permissionType === MemberAccessCheckType.System) {
      const cachedPermissions = systemCache.system;

      if (!cachedPermissions) {
        return false;
      }

      return check.checks.every((permission) => permission in cachedPermissions);
    }

    // Organization-specific permissions require orgId
    if (!organizationId) {
      return false;
    }

    const currentOrgCache = orgCache[organizationId];

    if (!currentOrgCache) {
      return false;
    }

    const cachedPermissions = currentOrgCache[permissionType as keyof PermissionCache];

    if (!cachedPermissions) {
      return false;
    }

    return check.checks.every((permission) => permission in cachedPermissions);
  });
}

/**
 * Merges organization and system permissions into a single cache object
 * @param orgCache - Organization-specific cache
 * @param systemCache - System-wide cache
 * @param organizationId - Current organization ID
 * @returns Merged permission cache
 */
function mergePermissionCaches(
  orgCache: OrganizationPermissionCache,
  systemCache: PermissionCache,
  organizationId?: string,
): PermissionCache {
  const currentOrgCache = organizationId ? orgCache[organizationId] || {} : {};

  return {
    ...currentOrgCache,
    system: systemCache.system || {},
  };
}

/**
 * Extracts permissions from the API response based on access check type
 * @param check - The access check to extract permissions for
 * @param permissions - The API response containing permissions
 * @returns Extracted permissions as a record
 */
function extractPermissions(
  check: MemberAccessCheckRequestItem,
  permissions: unknown,
): Record<string, boolean> {
  if (!check.checks) {
    return {};
  }

  const extractedPermissions: Record<string, boolean> = {};
  const permsAPI = permissions as Record<string, (permission: string) => boolean>;

  switch (check.accessType) {
    case MemberAccessCheckType.Organization:
      check.checks.forEach((permission) => {
        extractedPermissions[permission] = permsAPI.hasOrgPermission?.(permission) ?? false;
      });
      break;
    case MemberAccessCheckType.Workspace:
      // TODO: Implement workspace permission extraction when needed
      break;
    case MemberAccessCheckType.Product:
      // TODO: Implement product permission extraction when needed
      break;
    case MemberAccessCheckType.System:
      check.checks.forEach((permission) => {
        extractedPermissions[permission] = permsAPI.hasSystemPermission?.(permission) ?? false;
      });
      break;
    default:
      throw new Error(`Unknown access type: ${String(check.accessType)}`);
  }

  return extractedPermissions;
}

interface CacheRefs {
  orgCache: React.MutableRefObject<OrganizationPermissionCache>;
  systemCache: React.MutableRefObject<PermissionCache>;
}

/**
 * Updates the appropriate cache based on permission type
 * @param check - The access check that was processed
 * @param extractedPermissions - The permissions to cache
 * @param cacheRefs - Cache references object
 * @param organizationId - Current organization ID
 */
function updateCache(
  check: MemberAccessCheckRequestItem,
  extractedPermissions: Record<string, boolean>,
  cacheRefs: CacheRefs,
  organizationId?: string,
): void {
  const permissionType = check.accessType;

  if (!permissionType) {
    return;
  }

  if (permissionType === MemberAccessCheckType.System) {
    // System permissions go to the global system cache
    cacheRefs.systemCache.current.system = {
      ...cacheRefs.systemCache.current.system,
      ...extractedPermissions,
    };
  } else {
    // Organization-specific permissions go to the org-specific cache
    if (organizationId) {
      if (!cacheRefs.orgCache.current[organizationId]) {
        cacheRefs.orgCache.current[organizationId] = {};
      }
      const orgCache = cacheRefs.orgCache.current[organizationId];
      orgCache[permissionType as keyof PermissionCache] = {
        ...orgCache[permissionType as keyof PermissionCache],
        ...extractedPermissions,
      };
    }
  }
}

export const GlobalAccessContext = createContext<GlobalAccessContextType | undefined>(undefined);

interface GlobalAccessProviderProps {
  children: React.ReactNode;
}

/**
 * Global Access Provider that manages permission caching and loading
 *
 * This provider handles:
 * - Caching permissions by organization ID to prevent cross-organization permission leakage
 * - Separating system permissions from organization-specific permissions
 * - Managing loading states to prevent duplicate requests
 * - Clearing cache when users change (but preserving cache when switching organizations)
 */
export const GlobalAccessProvider: React.FC<GlobalAccessProviderProps> = ({ children }) => {
  const orgCacheRef = useRef<OrganizationPermissionCache>({});
  const systemCacheRef = useRef<PermissionCache>({});
  const loadingStateRef = useRef<LoadingState>({ requests: new Set() });

  const user = useUser();
  const { authorizedOrganizationId: organizationId } = useAuthProvider();
  const { checkPermissions } = useAccess();
  const userId = user?.sub;

  const getPermissions = useCallback((accessChecks: MemberAccessCheckRequestItem[]): PermissionCache | null => {
    const allCached = areAllPermissionsCached(
      accessChecks,
      orgCacheRef.current,
      systemCacheRef.current,
      organizationId || undefined,
    );

    if (!allCached) {
      return null;
    }

    return mergePermissionCaches(orgCacheRef.current, systemCacheRef.current, organizationId || undefined);
  }, [organizationId]);

  const isLoadingPermissions = useCallback(
    (accessChecks: MemberAccessCheckRequestItem[]): boolean => {
      const orgId = organizationId || undefined;
      const loadingKey = createLoadingKey(accessChecks, orgId);

      return loadingStateRef.current.requests.has(loadingKey);
    },
    [organizationId],
  );

  const requestPermissions = useCallback(
    async (accessChecks: MemberAccessCheckRequestItem[]): Promise<PermissionCache> => {
      const loadingKey = createLoadingKey(accessChecks, organizationId || undefined);

      // Check if already cached
      const cached = getPermissions(accessChecks);

      if (cached) {
        return cached;
      }

      // Check if already loading
      if (loadingStateRef.current.requests.has(loadingKey)) {
        return new Promise((resolve, reject) => {
          const ttl = 3000;
          let timeElapsed = 0;

          const checkComplete = () => {
            const result = getPermissions(accessChecks);

            if (result) {
              resolve(result);
            } else if (!loadingStateRef.current.requests.has(loadingKey)) {
              reject(new Error('Failed to load permissions'));
            } else if (timeElapsed < ttl) {
              const interval = 100;
              timeElapsed += interval;
              setTimeout(checkComplete, interval);
            } else {
              reject(new Error('Failed to load permissions'));
            }
          };
          checkComplete();
        });
      }

      // Start loading
      loadingStateRef.current.requests.add(loadingKey);

      try {
        // Validate prerequisites
        const hasSystemPermissions = accessChecks.some((check) => check.accessType === MemberAccessCheckType.System);

        if (!userId) {
          throw new Error('User not available');
        }

        if (!hasSystemPermissions && !organizationId) {
          throw new Error('Organization not available for non-system permissions');
        }

        // Fetch permissions from API
        const permissions = await checkPermissions(...accessChecks);

        // Process and cache each access check
        accessChecks.forEach((check) => {
          const extractedPermissions = extractPermissions(check, permissions);
          updateCache(
            check,
            extractedPermissions,
            { orgCache: orgCacheRef, systemCache: systemCacheRef },
            organizationId || undefined,
          );
        });

        // Return merged permissions
        return mergePermissionCaches(orgCacheRef.current, systemCacheRef.current, organizationId || undefined);
      } catch (err) {
        // Handle unauthorized errors gracefully
        const isUnauthorized = String(err).includes('Unauthorized');
        const errorMessage = isUnauthorized
          ? null // Unauthorized is expected, not an error
          : 'Permissions could not be checked at this time, please try again later.';

        if (errorMessage) {
          Sentry.captureException(err as Error, {
            tags: {
              component: 'GlobalAccessProvider',
              action: 'requestPermissions',
            },
            extra: {
              accessChecks,
              userId,
              organizationId,
            },
          });
          throw new Error(errorMessage);
        }

        // Return current cache state on error
        return mergePermissionCaches(orgCacheRef.current, systemCacheRef.current, organizationId || undefined);
      } finally {
        loadingStateRef.current.requests.delete(loadingKey);
      }
    },
    [userId, organizationId, checkPermissions, getPermissions],
  );

  const clearCache = useCallback(() => {
    orgCacheRef.current = {};
    systemCacheRef.current = {};
    loadingStateRef.current.requests.clear();
  }, []);

  const contextValue = useMemo(() => ({
    getPermissions,
    requestPermissions,
    isLoadingPermissions,
    clearCache,
  }), [getPermissions, requestPermissions, isLoadingPermissions, clearCache]);

  return (
    <GlobalAccessContext.Provider value={contextValue}>
      {children}
    </GlobalAccessContext.Provider>
  );
};

/**
 * Hook to access the global access context
 * @returns The global access context
 * @throws Error if used outside of GlobalAccessProvider
 */
export function useGlobalAccess(): GlobalAccessContextType {
  const context = useContext(GlobalAccessContext);

  if (context === undefined) {
    throw new Error('useGlobalAccess must be used within a GlobalAccessProvider');
  }

  return context;
}
